import { alpha, darken, lighten, Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";

type Sx = SystemStyleObject<Theme>;

export const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

export const styless = (theme: Theme) => {
  const dark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;
  const linha = theme.palette.divider;
  const sombra = dark
    ? "0px 1px 2px rgba(0,0,0,0.45)"
    : "0px 1px 3px rgba(0,0,0,0.06)";
  const sombraAlta = dark
    ? "0px 10px 28px rgba(0,0,0,0.55)"
    : "0px 10px 28px rgba(0,0,0,0.10)";

  return {
    page: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      width: "100%",
      minWidth: 0,
    } as Sx,

    header: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 2.5,
      p: { xs: 2, sm: 2.25 },
      boxShadow: sombra,
      background: dark
        ? `linear-gradient(125deg, ${alpha(primary, 0.16)} 0%, ${alpha(
            primary,
            0.04,
          )} 55%, ${theme.palette.background.paper} 100%)`
        : `linear-gradient(125deg, ${alpha(primary, 0.16)} 0%, ${alpha(
            primary,
            0.05,
          )} 45%, ${theme.palette.background.paper} 100%)`,
    } as Sx,
    headerGlow: {
      position: "absolute",
      borderRadius: "50%",
      background: alpha(primary, dark ? 0.14 : 0.16),
      filter: "blur(6px)",
      pointerEvents: "none",
    } as Sx,
    headerTop: {
      position: "relative",
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      flexDirection: { xs: "column", sm: "row" },
      gap: 2,
    } as Sx,
    headerIdent: {
      display: "flex",
      alignItems: "center",
      gap: 1.75,
      minWidth: 0,
    } as Sx,
    headerIcon: {
      width: 48,
      height: 48,
      borderRadius: 2,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: primary,
      background: `linear-gradient(135deg, ${alpha(primary, 0.2)}, ${alpha(
        primary,
        0.07,
      )})`,
      boxShadow: `inset 0 0 0 1px ${alpha(primary, 0.16)}`,
    } as Sx,
    headerTitulo: {
      fontSize: { xs: "1.15rem", sm: "1.3rem" },
      fontWeight: 600,
      lineHeight: 1.2,
      color: theme.palette.text.primary,
    } as Sx,
    headerSubtitulo: {
      fontSize: "0.85rem",
      color: theme.palette.text.secondary,
      mt: 0.25,
    } as Sx,
    headerAcao: {
      display: "flex",
      alignItems: "center",
      gap: 1.75,
      width: { xs: "100%", sm: "auto" },
    } as Sx,
    headerDivisor: {
      display: { xs: "none", sm: "block" },
      alignSelf: "stretch",
      width: "1px",
      minHeight: 34,
      backgroundColor: alpha(primary, dark ? 0.35 : 0.22),
    } as Sx,
    headerCta: {
      flexShrink: 0,
      borderRadius: 2,
      px: 2.5,
      py: 1,
      fontSize: "0.86rem",
      fontWeight: 700,
      width: { xs: "100%", sm: "auto" },
      background: `linear-gradient(135deg, ${primary} 0%, ${
        dark ? lighten(primary, 0.14) : darken(primary, 0.08)
      } 100%)`,
      transition: "transform .18s ease, box-shadow .18s ease, filter .18s ease",
      boxShadow: `0 4px 14px ${alpha(primary, dark ? 0.45 : 0.32)}`,
      "&:hover": {
        transform: "translateY(-1px)",
        filter: "brightness(1.05)",
        boxShadow: `0 8px 20px ${alpha(primary, dark ? 0.55 : 0.4)}`,
      },
      "&:active": { transform: "translateY(0) scale(.98)" },
      [REDUCED_MOTION]: {
        transition: "none",
        "&:hover": { transform: "none" },
      },
    } as Sx,

    barra: {
      display: "flex",
      flexDirection: "column",
      gap: 1.1,
      p: 1.25,
      borderRadius: 2.5,
      boxShadow: sombra,
    } as Sx,
    tabsRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1,
      width: "100%",
    } as Sx,
    segmented: {
      display: "flex",
      alignItems: "center",
      gap: 0.25,
      p: 0.35,
      borderRadius: 2.25,
      flexShrink: 0,
      backgroundColor: alpha(theme.palette.text.primary, dark ? 0.07 : 0.045),
    } as Sx,
    segmentedItem: (ativo: boolean) =>
      ({
        display: "inline-flex",
        alignItems: "center",
        gap: 0.85,
        height: 34,
        px: 1.75,
        borderRadius: 1.85,
        fontSize: "0.83rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: ativo
          ? dark
            ? lighten(primary, 0.22)
            : darken(primary, 0.06)
          : theme.palette.text.secondary,
        background: ativo
          ? `linear-gradient(135deg, ${alpha(primary, dark ? 0.32 : 0.16)}, ${alpha(
              primary,
              dark ? 0.2 : 0.09,
            )})`
          : "transparent",
        boxShadow: ativo
          ? `inset 0 0 0 1px ${alpha(primary, dark ? 0.45 : 0.28)}, 0 2px 10px ${alpha(
              primary,
              dark ? 0.32 : 0.16,
            )}`
          : "none",
        transition: "all .2s cubic-bezier(.4,0,.2,1)",
        "&:hover": {
          color: ativo
            ? dark
              ? lighten(primary, 0.28)
              : darken(primary, 0.1)
            : theme.palette.text.primary,
          backgroundColor: ativo
            ? undefined
            : alpha(theme.palette.text.primary, dark ? 0.09 : 0.055),
        },
        "&:active": { transform: "scale(.97)" },
        [REDUCED_MOTION]: {
          transition: "none",
          "&:active": { transform: "none" },
        },
      }) as Sx,
    segmentedContagem: (ativo: boolean) =>
      ({
        minWidth: 20,
        height: 18,
        px: 0.6,
        borderRadius: 999,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.68rem",
        fontWeight: 800,
        lineHeight: 1,
        backgroundColor: ativo
          ? primary
          : alpha(theme.palette.text.primary, dark ? 0.15 : 0.08),
        color: ativo ? "#fff" : theme.palette.text.secondary,
        transition: "background-color .2s ease, color .2s ease",
        [REDUCED_MOTION]: { transition: "none" },
      }) as Sx,
    arquivoBotao: (ativo: boolean) =>
      ({
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        height: 32,
        px: 1.3,
        borderRadius: 2,
        fontSize: "0.8rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        flexShrink: 0,
        color: ativo ? primary : theme.palette.text.secondary,
        backgroundColor: ativo ? alpha(primary, dark ? 0.18 : 0.1) : "transparent",
        border: `1px solid ${ativo ? alpha(primary, dark ? 0.5 : 0.32) : "transparent"}`,
        transition: "all .2s ease",
        "&:hover": {
          color: primary,
          backgroundColor: alpha(primary, dark ? 0.16 : 0.08),
          borderColor: alpha(primary, 0.3),
        },
        "&:active": { transform: "scale(.97)" },
        [REDUCED_MOTION]: {
          transition: "none",
          "&:active": { transform: "none" },
        },
      }) as Sx,
    filtrosDivisor: {
      width: "100%",
      height: "1px",
      backgroundColor: linha,
    } as Sx,
    filtrosRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1.25,
      width: "100%",
    } as Sx,
    contadorSecao: {
      minWidth: 20,
      height: 18,
      px: 0.65,
      borderRadius: 2,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "0.68rem",
      fontWeight: 700,
      lineHeight: 1,
      backgroundColor: alpha(primary, dark ? 0.3 : 0.13),
      color: dark ? lighten(primary, 0.3) : primary,
    } as Sx,
    busca: {
      flex: "1 1 200px",
      minWidth: 0,
      "& .MuiOutlinedInput-root": { borderRadius: 2, height: 36 },
    } as Sx,
    filtroStatus: {
      flex: "0 1 240px",
      minWidth: 170,
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        minHeight: 36,
        py: 0.15,
        pl: 1.5,
      },
    } as Sx,
    filtroPill: (ativo: boolean) =>
      ({
        height: 40,
        px: 1.75,
        borderRadius: 2,
        textTransform: "none",
        fontSize: "0.82rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        transition: "all .2s ease",
        color: ativo
          ? dark
            ? lighten(primary, 0.25)
            : primary
          : theme.palette.text.secondary,
        borderColor: ativo ? alpha(primary, 0.55) : linha,
        backgroundColor: ativo
          ? alpha(primary, dark ? 0.2 : 0.1)
          : "transparent",
        "&:hover": {
          borderColor: alpha(primary, 0.6),
          backgroundColor: alpha(primary, dark ? 0.24 : 0.08),
        },
        [REDUCED_MOTION]: { transition: "none" },
      }) as Sx,
    limparFiltros: {
      width: 36,
      height: 40,
      color: theme.palette.text.secondary,
      transition: "color .2s ease, background-color .2s ease",
      "&:hover": {
        color: theme.palette.error.main,
        backgroundColor: alpha(theme.palette.error.main, 0.1),
      },
      [REDUCED_MOTION]: { transition: "none" },
    } as Sx,
    periodoPopover: {
      p: 2,
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      width: 300,
      borderRadius: 2,
      boxShadow: sombraAlta,
    } as Sx,
    periodoPresets: {
      display: "flex",
      flexWrap: "wrap",
      gap: 0.75,
    } as Sx,
    periodoPreset: {
      height: 26,
      borderRadius: 2,
      fontSize: "0.75rem",
      fontWeight: 500,
      cursor: "pointer",
      backgroundColor: alpha(primary, 0.08),
      color: primary,
      "&:hover": { backgroundColor: alpha(primary, 0.16) },
    } as Sx,

    tabelaPaper: {
      borderRadius: 2.5,
      overflow: "hidden",
      boxShadow: sombra,
    } as Sx,
    tabelaScroll: {
      width: "100%",
      overflow: "auto",
      maxHeight: "min(64vh, 760px)",
      scrollbarWidth: "thin",
      scrollbarColor: `${alpha(theme.palette.text.primary, 0.22)} transparent`,
      "&::-webkit-scrollbar": { width: 8, height: 8 },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: 8,
        backgroundColor: alpha(theme.palette.text.primary, 0.18),
        border: "2px solid transparent",
        backgroundClip: "content-box",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: alpha(theme.palette.text.primary, 0.32),
      },
    } as Sx,
    tabela: {
      minWidth: 900,
      borderCollapse: "separate",
      borderSpacing: 0,
      "& td": { border: 0 },
    } as Sx,
    thead: {
      "& th": {
        position: "sticky",
        top: 0,
        zIndex: 2,
        py: 1.35,
        border: 0,
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: theme.palette.text.disabled,
        whiteSpace: "nowrap",
        backgroundColor: dark
          ? alpha(theme.palette.background.paper, 0.92)
          : alpha(theme.palette.background.paper, 0.88),
        backdropFilter: "blur(8px)",
        boxShadow: `inset 0 -1px 0 ${linha}`,
      },
      "& th:first-of-type": { pl: 2.5 },
      "& th:last-of-type": { pr: 2.5 },
      "& .MuiTableSortLabel-root": { transition: "color .18s ease" },
      "& .MuiTableSortLabel-root:hover": { color: primary },
      "& .MuiTableSortLabel-root.Mui-active": {
        color: primary,
        "& .MuiTableSortLabel-icon": { color: primary, opacity: 1 },
      },
    } as Sx,
    linha: (vencida: boolean) =>
      ({
        cursor: "pointer",
        position: "relative",
        transition: "background-color .18s ease",
        animation: "suporteFadeUp .34s cubic-bezier(.22,1,.36,1) both",
        animationDelay: "calc(var(--i, 0) * 26ms)",
        "@keyframes suporteFadeUp": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "none" },
        },
        "& td": { boxShadow: `inset 0 -1px 0 ${linha}` },
        "& td:first-of-type": {
          pl: 2.5,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 7,
            bottom: 7,
            width: vencida ? 3 : 0,
            borderRadius: "0 4px 4px 0",
            backgroundColor: vencida ? theme.palette.error.main : primary,
            transition: "width .2s cubic-bezier(.22,1,.36,1)",
          },
        },
        "& td:last-of-type": { pr: 2.5 },
        "& .linha-seta, & .linha-acao": {
          opacity: 0,
          transform: "translateX(-6px)",
          transition: "opacity .2s ease, transform .2s ease",
        },
        "&:hover, &:focus-within": {
          backgroundColor: alpha(primary, dark ? 0.12 : 0.055),
          "& td:first-of-type::before": { width: 3 },
          "& .linha-seta, & .linha-acao": { opacity: 1, transform: "none" },
        },
        "&:last-of-type td": { boxShadow: "none" },
        [REDUCED_MOTION]: {
          animation: "none",
          transition: "none",
          "& td:first-of-type::before": { transition: "none" },
          "& .linha-seta, & .linha-acao": { transition: "none" },
        },
      }) as Sx,
    celula: {
      py: 1.35,
      fontSize: "0.85rem",
      color: theme.palette.text.primary,
    } as Sx,
    setaLinha: {
      display: "flex",
      justifyContent: "flex-end",
      color: primary,
    } as Sx,
    acoesLinha: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 0.25,
    } as Sx,
    botaoArquivar: {
      color: theme.palette.text.disabled,
      "&:hover": {
        color: primary,
        backgroundColor: alpha(primary, dark ? 0.18 : 0.09),
      },
    } as Sx,
    botaoArquivarTexto: {
      height: 30,
      px: 1.4,
      borderRadius: 2,
      fontSize: "0.78rem",
      fontWeight: 700,
      textTransform: "none",
      whiteSpace: "nowrap",
      flexShrink: 0,
      color: theme.palette.text.secondary,
      borderColor: linha,
      transition: "color .18s ease, border-color .18s ease, background-color .18s ease",
      "&:hover": {
        color: primary,
        borderColor: alpha(primary, 0.45),
        backgroundColor: alpha(primary, dark ? 0.14 : 0.07),
      },
      [REDUCED_MOTION]: { transition: "none" },
    } as Sx,
    selo: (cor: string) =>
      ({
        display: "inline-flex",
        alignItems: "center",
        gap: 0.45,
        height: 22,
        px: 0.9,
        borderRadius: 999,
        flexShrink: 0,
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        color: dark ? lighten(cor, 0.55) : darken(cor, 0.2),
        backgroundColor: alpha(cor, dark ? 0.32 : 0.14),
        border: `1px solid ${alpha(cor, dark ? 0.55 : 0.3)}`,
      }) as Sx,
    rodapeTabela: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      py: 1.5,
      fontSize: "0.78rem",
      color: theme.palette.text.disabled,
      borderTop: `1px solid ${linha}`,
    } as Sx,
    codigo: {
      display: "inline-block",
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: "0.72rem",
      fontWeight: 600,
      letterSpacing: "0.02em",
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.text.primary, dark ? 0.1 : 0.05),
      border: `1px solid ${linha}`,
      borderRadius: 1,
      px: 1,
      py: 0.35,
      whiteSpace: "nowrap",
    } as Sx,
    titulo: {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: 340,
    } as Sx,
    tituloComBadge: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
    } as Sx,
    comentariosBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.4,
      flexShrink: 0,
      fontSize: "0.72rem",
      fontWeight: 600,
      color: theme.palette.text.disabled,
    } as Sx,
    responsavel: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
    } as Sx,
    dataVencida: {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      color: theme.palette.error.main,
      fontWeight: 600,
    } as Sx,
    cardsScroll: {
      maxHeight: "min(66vh, 720px)",
      overflowY: "auto",
      scrollbarWidth: "thin",
    } as Sx,
    cardsLista: {
      display: "flex",
      flexDirection: "column",
      gap: 1.25,
    } as Sx,
    card: (cor: string) =>
      ({
        position: "relative",
        overflow: "hidden",
        p: 1.75,
        pl: 2.25,
        borderRadius: 2,
        cursor: "pointer",
        boxShadow: sombra,
        transition: "transform .18s ease, box-shadow .18s ease",
        animation: "suporteFadeUp .32s ease both",
        animationDelay: "calc(var(--i, 0) * 30ms)",
        "@keyframes suporteFadeUp": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "none" },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: cor,
        },
        "&:active": { transform: "scale(.995)" },
        "&:hover": { boxShadow: sombraAlta },
        [REDUCED_MOTION]: { animation: "none", transition: "none" },
      }) as Sx,
    cardTopo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      mb: 0.85,
    } as Sx,
    cardTitulo: {
      fontSize: "0.9rem",
      fontWeight: 600,
      lineHeight: 1.35,
      color: theme.palette.text.primary,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    } as Sx,
    cardRodape: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1.25,
      mt: 1.25,
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
    } as Sx,

    vazio: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.5,
      py: 7,
      px: 2,
      textAlign: "center",
    } as Sx,
    vazioTitulo: {
      fontSize: "0.95rem",
      fontWeight: 600,
      color: theme.palette.text.secondary,
    } as Sx,
    vazioTexto: {
      fontSize: "0.83rem",
      color: theme.palette.text.disabled,
      maxWidth: 380,
    } as Sx,

    drawerPaper: {
      width: { xs: "100%", sm: 640, mdl: 860, xlf: 960 },
      maxWidth: "100%",
      borderRadius: { xs: 0, sm: "16px 0 0 16px" },
      backgroundImage: "none",
      backgroundColor: theme.palette.background.paper,
    } as Sx,
    drawerRoot: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
    } as Sx,
    drawerHeader: {
      flexShrink: 0,
      position: "relative",
      overflow: "hidden",
      px: { xs: 2, sm: 2.75 },
      pt: 1.5,
      pb: 1.75,
      borderBottom: `1px solid ${linha}`,
      background: dark
        ? `linear-gradient(150deg, ${alpha(primary, 0.22)} 0%, ${alpha(
            primary,
            0.05,
          )} 60%, transparent 100%)`
        : `linear-gradient(150deg, ${alpha(primary, 0.22)} 0%, ${alpha(
            primary,
            0.06,
          )} 60%, transparent 100%)`,
    } as Sx,
    drawerHeaderTopo: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1,
      mb: 1.25,
    } as Sx,
    drawerTitulo: {
      fontSize: { xs: "1.1rem", sm: "1.25rem" },
      fontWeight: 650,
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
      color: theme.palette.text.primary,
      wordBreak: "break-word",
    } as Sx,
    metaStrip: {
      display: "flex",
      flexWrap: "wrap",
      gap: { xs: 1.25, sm: 2.75 },
      mt: 1.5,
    } as Sx,
    metaItem: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
    } as Sx,
    metaIcone: {
      width: 30,
      height: 30,
      borderRadius: "50%",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: primary,
      backgroundColor: alpha(primary, dark ? 0.24 : 0.11),
    } as Sx,
    metaLabel: {
      fontSize: "0.66rem",
      fontWeight: 700,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: theme.palette.text.disabled,
      lineHeight: 1.3,
    } as Sx,
    metaValor: {
      fontSize: "0.85rem",
      fontWeight: 600,
      lineHeight: 1.3,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      whiteSpace: "nowrap",
    } as Sx,
    drawerScroll: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      px: { xs: 2, sm: 2.75 },
      py: 2,
      display: "flex",
      flexDirection: "column",
      gap: 1.75,
      "& > *": { flexShrink: 0 },
      scrollbarWidth: "thin",
      scrollbarColor: `${alpha(theme.palette.text.primary, 0.22)} transparent`,
      "&::-webkit-scrollbar": { width: 8 },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: 8,
        backgroundColor: alpha(theme.palette.text.primary, 0.18),
        border: "2px solid transparent",
        backgroundClip: "content-box",
      },
    } as Sx,
    categoriasLinha: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 0.6,
    } as Sx,
    categoriaChip: {
      height: 23,
      fontSize: "0.72rem",
      fontWeight: 600,
      borderRadius: 2,
      backgroundColor: alpha(primary, dark ? 0.24 : 0.11),
      color: dark ? lighten(primary, 0.28) : primary,
    } as Sx,

    cardResumo: (cor: string) =>
      ({
        position: "relative",
        flexShrink: 0,
        borderRadius: 2.5,
        backgroundColor: alpha(cor, dark ? 0.1 : 0.05),
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          borderRadius: "10px 0 0 10px",
          backgroundColor: cor,
          opacity: 0.85,
        },
      }) as Sx,
    cardResumoTopo: (cor: string) =>
      ({
        display: "flex",
        alignItems: "center",
        gap: 1.15,
        pl: 2,
        pr: 1.25,
        py: 1.05,
        borderRadius: 2.5,
        cursor: "pointer",
        userSelect: "none",
        color: dark ? lighten(cor, 0.35) : cor,
        transition: "background-color .16s ease",
        "&:hover": { backgroundColor: alpha(cor, 0.09) },
        [REDUCED_MOTION]: { transition: "none" },
      }) as Sx,
    cardResumoTitulo: {
      fontSize: "0.8rem",
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    } as Sx,
    cardResumoMeta: {
      fontSize: "0.72rem",
      color: theme.palette.text.disabled,
      ml: "auto",
      whiteSpace: "nowrap",
    } as Sx,
    cardResumoCorpo: {
      pl: 2,
      pr: 2,
      pb: 1.75,
      pt: 0.25,
      display: "flex",
      flexDirection: "column",
      gap: 1.25,
    } as Sx,
    blocoLabel: {
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.disabled,
      mb: 0.5,
    } as Sx,
    blocoVazio: {
      fontSize: "0.83rem",
      fontStyle: "italic",
      color: theme.palette.text.disabled,
    } as Sx,

    secaoTitulo: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      fontSize: "0.8rem",
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    } as Sx,
    conversa: {
      display: "flex",
      flexDirection: "column",
      gap: 1.75,
    } as Sx,
    grupoMensagem: {
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
    } as Sx,
    mensagemLinha: (proprio: boolean) =>
      ({
        display: "flex",
        gap: 1.25,
        alignItems: "flex-end",
        flexDirection: proprio ? "row-reverse" : "row",
        animation: "suporteBolha .34s cubic-bezier(.22,1,.36,1) both",
        "@keyframes suporteBolha": {
          from: {
            opacity: 0,
            transform: `translateY(10px) translateX(${proprio ? "8px" : "-8px"})`,
          },
          to: { opacity: 1, transform: "none" },
        },
        [REDUCED_MOTION]: { animation: "none" },
      }) as Sx,
    bolhaColuna: (proprio: boolean) =>
      ({
        display: "flex",
        flexDirection: "column",
        alignItems: proprio ? "flex-end" : "flex-start",
        gap: 0.5,
        minWidth: 0,
        maxWidth: "min(86%, 520px)",
      }) as Sx,
    bolhaMeta: (proprio: boolean) =>
      ({
        display: "flex",
        alignItems: "center",
        gap: 0.85,
        px: 0.5,
        flexDirection: proprio ? "row-reverse" : "row",
      }) as Sx,
    bolhaAutor: {
      fontSize: "0.78rem",
      fontWeight: 700,
      color: theme.palette.text.secondary,
    } as Sx,
    bolhaHora: {
      fontSize: "0.7rem",
      color: theme.palette.text.disabled,
    } as Sx,
    bolha: (proprio: boolean, resposta: boolean) =>
      ({
        position: "relative",
        width: "100%",
        px: resposta ? 1.4 : 1.6,
        py: resposta ? 1 : 1.2,
        borderRadius: 2.5,
        borderStartStartRadius: proprio ? 20 : 6,
        borderStartEndRadius: proprio ? 6 : 20,
        backgroundColor: proprio
          ? alpha(primary, dark ? 0.26 : 0.1)
          : dark
            ? alpha(theme.palette.common.white, 0.06)
            : theme.palette.background.textField,
        boxShadow: proprio
          ? `inset 0 0 0 1px ${alpha(primary, dark ? 0.4 : 0.2)}`
          : `inset 0 0 0 1px ${linha}`,
        transition: "box-shadow .18s ease",
        "&:hover .bolha-acoes": { opacity: 1 },
      }) as Sx,
    bolhaAcoes: {
      position: "absolute",
      top: -10,
      right: -8,
      opacity: 0,
      transition: "opacity .18s ease",
      "& .MuiIconButton-root": {
        backgroundColor: theme.palette.background.paper,
        boxShadow: sombra,
        border: `1px solid ${linha}`,
        "&:hover": {
          backgroundColor: alpha(primary, dark ? 0.18 : 0.08),
          borderColor: alpha(primary, 0.4),
        },
      },
      [REDUCED_MOTION]: { transition: "none" },
    } as Sx,
    corpoHtml: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
      color: theme.palette.text.primary,
      wordBreak: "break-word",
      "& p": { m: 0 },
      "& p + p": { mt: 0.85 },
      "& ul, & ol": { m: 0, pl: 2.5 },
      "& a": {
        color: theme.palette.cromatic.blue.main,
        textDecoration: "none",
        fontWeight: 600,
        "&:hover": { textDecoration: "underline" },
      },
      "& img": {
        display: "block",
        width: 120,
        height: 120,
        objectFit: "cover",
        borderRadius: 2,
        border: `1px solid ${linha}`,
        my: 1,
        cursor: "zoom-in",
        transition:
          "transform .2s cubic-bezier(.22,1,.36,1), box-shadow .2s ease",
        "&:hover": { transform: "scale(1.04)", boxShadow: sombraAlta },
        [REDUCED_MOTION]: {
          transition: "none",
          "&:hover": { transform: "none" },
        },
      },
    } as Sx,
    respostasTrilho: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      gap: 1,
      mt: 0.75,
      ml: { xs: 1.75, sm: 3.5 },
      pl: { xs: 1.5, sm: 2 },
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 4,
        bottom: 18,
        width: "2px",
        borderRadius: 2,
        backgroundColor: alpha(primary, dark ? 0.32 : 0.2),
      },
    } as Sx,
    responderBotao: {
      alignSelf: "flex-start",
      ml: { xs: 1.75, sm: 3.5 },
      mt: 0.25,
      height: 26,
      px: 1.25,
      borderRadius: 2,
      fontSize: "0.76rem",
      fontWeight: 600,
      textTransform: "none",
      color: theme.palette.text.secondary,
      transition: "color .18s ease, background-color .18s ease",
      "&:hover": {
        color: primary,
        backgroundColor: alpha(primary, dark ? 0.18 : 0.08),
      },
      [REDUCED_MOTION]: { transition: "none" },
    } as Sx,
    respostaComposer: {
      ml: { xs: 1.75, sm: 3.5 },
      mt: 0.75,
    } as Sx,
    conversaVazia: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
      py: 4,
      px: 2,
      textAlign: "center",
      borderRadius: 2.5,
      border: `1px dashed ${linha}`,
      color: theme.palette.text.disabled,
    } as Sx,
    docaComposer: {
      flexShrink: 0,
      px: { xs: 2, sm: 2.75 },
      py: 1.5,
      borderTop: `1px solid ${linha}`,
      backgroundColor: dark
        ? alpha(theme.palette.common.white, 0.02)
        : alpha(theme.palette.text.primary, 0.015),
    } as Sx,
    encerrada: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      py: 1.5,
      fontSize: "0.82rem",
      fontWeight: 500,
      color: theme.palette.text.disabled,
    } as Sx,

    composer: {
      display: "flex",
      flexDirection: "column",
      gap: 1,
      p: 1.5,
      borderRadius: 2.5,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `inset 0 0 0 1px ${linha}`,
      transition: "box-shadow .2s ease",
      "&:focus-within": {
        boxShadow: `inset 0 0 0 1px ${alpha(primary, 0.6)}, 0 0 0 4px ${alpha(
          primary,
          dark ? 0.18 : 0.1,
        )}`,
      },
      [REDUCED_MOTION]: { transition: "none" },
    } as Sx,
    composerAcoes: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
    } as Sx,
    composerDica: {
      fontSize: "0.7rem",
      color: theme.palette.text.disabled,
    } as Sx,
    dockGatilho: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent",
        boxShadow: "none",
        px: 0,
        fontSize: "0.85rem",
      },
    } as Sx,

    editor: {
      "& .ql-container.ql-snow": {
        border: "none",
        fontFamily: theme.typography.fontFamily,
        fontSize: "0.875rem",
      },
      "& .ql-editor": {
        p: 0,
        minHeight: 56,
        maxHeight: 240,
        overflowY: "auto",
        color: theme.palette.text.primary,
        lineHeight: 1.55,
        "& p": { margin: 0 },
        "& ul, & ol": { paddingLeft: "1.5em" },
        "& img": {
          display: "block",
          width: 84,
          height: 84,
          objectFit: "cover",
          borderRadius: 6,
          border: `1px solid ${linha}`,
          margin: "8px 0",
        },
        "& a": { color: theme.palette.cromatic.blue.main },
      },
      "& .ql-editor.ql-blank::before": {
        left: 0,
        right: 0,
        color: theme.palette.text.disabled,
        fontStyle: "normal",
        fontSize: "0.875rem",
      },
      "& .ql-toolbar.ql-snow": {
        border: "none",
        borderBottom: `1px solid ${linha}`,
        padding: "0 0 6px 0",
        marginBottom: "8px",
        "& .ql-formats": { marginRight: "10px" },
        "& button": {
          width: 26,
          height: 24,
          borderRadius: 4,
          transition: "background-color .15s ease",
          "& .ql-stroke": { stroke: alpha(theme.palette.text.primary, 0.6) },
          "& .ql-fill": { fill: alpha(theme.palette.text.primary, 0.6) },
        },
        "& button:hover": { backgroundColor: alpha(primary, 0.1) },
        "& button:hover .ql-stroke, & button.ql-active .ql-stroke": {
          stroke: primary,
        },
        "& button:hover .ql-fill, & button.ql-active .ql-fill": {
          fill: primary,
        },
      },
      "& .ql-tooltip": {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${linha}`,
        boxShadow: sombraAlta,
        borderRadius: "8px",
        zIndex: 1500,
        "& input": {
          backgroundColor: theme.palette.background.textField,
          color: theme.palette.text.primary,
          border: `1px solid ${linha}`,
          borderRadius: 4,
        },
        "& a": { color: primary },
      },
    } as Sx,
    editorCompacto: {
      "& .ql-toolbar.ql-snow": { display: "none" },
      "& .ql-editor": { minHeight: 32 },
    } as Sx,

    anexosLinha: {
      display: "flex",
      flexWrap: "wrap",
      gap: 1,
    } as Sx,
    miniatura: {
      position: "relative",
      width: 68,
      height: 68,
      borderRadius: 1.5,
      overflow: "hidden",
      border: `1px solid ${linha}`,
      cursor: "zoom-in",
      transition: "transform .18s ease, box-shadow .18s ease",
      "& img": { width: "100%", height: "100%", objectFit: "cover" },
      "&:hover": { transform: "translateY(-2px)", boxShadow: sombraAlta },
      [REDUCED_MOTION]: {
        transition: "none",
        "&:hover": { transform: "none" },
      },
    } as Sx,
    miniaturaRemover: {
      position: "absolute",
      top: 3,
      right: 3,
      p: "2px",
      backgroundColor: alpha("#000", 0.55),
      color: "#fff",
      "&:hover": { backgroundColor: alpha("#000", 0.78) },
    } as Sx,
    arquivoChip: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 1,
      maxWidth: 240,
      py: 0.75,
      pl: 1,
      pr: 3.5,
      borderRadius: 1.5,
      border: `1px solid ${linha}`,
      backgroundColor: theme.palette.background.paper,
      cursor: "pointer",
      transition: "border-color .16s ease, background-color .16s ease",
      "&:hover": {
        borderColor: alpha(primary, 0.5),
        backgroundColor: alpha(primary, 0.05),
      },
      [REDUCED_MOTION]: { transition: "none" },
    } as Sx,
    arquivoIcone: {
      fontSize: 22,
      color: primary,
      flexShrink: 0,
    } as Sx,
    arquivoNome: {
      fontSize: "0.78rem",
      lineHeight: 1.25,
      fontWeight: 500,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    } as Sx,
    arquivoTamanho: {
      fontSize: "0.68rem",
      lineHeight: 1.25,
      color: theme.palette.text.secondary,
    } as Sx,
    arquivoRemover: {
      position: "absolute",
      top: "50%",
      right: 3,
      transform: "translateY(-50%)",
      p: "2px",
    } as Sx,

    modalPaper: {
      borderRadius: 3,
      backgroundImage: "none",
      width: "100%",
      overflow: "hidden",
    } as Sx,
    modalCabecalho: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2.75,
      py: 2.15,
      borderBottom: `1px solid ${linha}`,
      background: dark
        ? `linear-gradient(135deg, ${alpha(primary, 0.22)} 0%, ${alpha(
            primary,
            0.04,
          )} 65%, transparent 100%)`
        : `linear-gradient(135deg, ${alpha(primary, 0.15)} 0%, ${alpha(
            primary,
            0.04,
          )} 65%, transparent 100%)`,
    } as Sx,
    modalCabecalhoIcone: {
      width: 42,
      height: 42,
      borderRadius: 2,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: primary,
      background: `linear-gradient(135deg, ${alpha(primary, 0.22)}, ${alpha(
        primary,
        0.08,
      )})`,
      boxShadow: `inset 0 0 0 1px ${alpha(primary, 0.18)}`,
    } as Sx,
    modalCabecalhoTextos: {
      minWidth: 0,
      flex: 1,
    } as Sx,
    modalCabecalhoTitulo: {
      fontSize: "1.05rem",
      fontWeight: 650,
      lineHeight: 1.3,
      color: theme.palette.text.primary,
    } as Sx,
    modalCabecalhoSubtitulo: {
      fontSize: "0.78rem",
      color: theme.palette.text.secondary,
      mt: 0.15,
    } as Sx,
    modalFechar: {
      flexShrink: 0,
      color: theme.palette.text.secondary,
      transition: "color .18s ease, background-color .18s ease",
      "&:hover": {
        color: theme.palette.error.main,
        backgroundColor: alpha(theme.palette.error.main, 0.1),
      },
      [REDUCED_MOTION]: { transition: "none" },
    } as Sx,
    modalConteudo: {
      px: 2.75,
      py: 2.5,
      display: "flex",
      flexDirection: "column",
    } as Sx,
    campoLabel: {
      fontSize: "0.72rem",
      fontWeight: 700,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      mb: 0.85,
    } as Sx,
    modalNota: {
      display: "flex",
      alignItems: "flex-start",
      gap: 1.25,
      p: 1.5,
      borderRadius: 1.75,
      backgroundColor: alpha(theme.palette.info.main, dark ? 0.12 : 0.07),
      border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
      fontSize: "0.8rem",
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
    } as Sx,
    modalAcoes: {
      px: 2.75,
      py: 2,
      gap: 1,
      borderTop: `1px solid ${linha}`,
    } as Sx,
    modalCtaPrimaria: {
      borderRadius: 2,
      px: 2.75,
      py: 0.95,
      fontSize: "0.85rem",
      fontWeight: 700,
      textTransform: "none",
      background: `linear-gradient(135deg, ${primary} 0%, ${
        dark ? lighten(primary, 0.14) : darken(primary, 0.08)
      } 100%)`,
      boxShadow: `0 4px 14px ${alpha(primary, dark ? 0.45 : 0.32)}`,
      transition: "transform .18s ease, box-shadow .18s ease, filter .18s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        filter: "brightness(1.05)",
        boxShadow: `0 8px 20px ${alpha(primary, dark ? 0.55 : 0.4)}`,
      },
      "&:active": { transform: "translateY(0) scale(.98)" },
      "&.Mui-disabled": {
        background: alpha(theme.palette.text.primary, dark ? 0.12 : 0.08),
        boxShadow: "none",
        color: theme.palette.text.disabled,
      },
      [REDUCED_MOTION]: {
        transition: "none",
        "&:hover": { transform: "none" },
      },
    } as Sx,

    skeletonLinha: {
      display: "flex",
      alignItems: "center",
      gap: 2,
      px: 2,
      py: 1.35,
      borderBottom: `1px solid ${linha}`,
    } as Sx,
  };
};

export type SuporteStyles = ReturnType<typeof styless>;
